import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import Alert from '../common/Alert.js';
import vehicleService from '../../services/vehicleService.js';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

export default function VehicleList() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    search: '',
    status: '',
    vehicle_type: '',
  });
  const perPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchVehicles(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchVehicles(currentPage);
  }, [currentPage]);

  const fetchVehicles = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.search) filters.search = filterValues.search;
      if (filterValues.status) filters.status = filterValues.status;
      if (filterValues.vehicle_type) filters.vehicle_type = filterValues.vehicle_type;

      const response = await vehicleService.getAllVehicles(page, perPage, filters);
      setVehicles(response.data.data);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load vehicles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vehicleId, vehicleName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${vehicleName}"? This action cannot be undone.`,
      'Delete Vehicle'
    );
    if (!confirmed) return;

    try {
      const response = await vehicleService.deleteVehicle(vehicleId);
      Alert.success(response.data?.message || 'Vehicle deleted successfully');

      if (vehicles.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchVehicles();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete vehicle');
    }
  };

  const columns = [
    {
      key: 'vehicle_name',
      label: 'Vehicle Name',
      render: (vehicle) => (
        <button
          onClick={() => navigate(`/vehicles/view/${vehicle.id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {vehicle.vehicle_name}
        </button>
      ),
    },
    { key: 'vehicle_number', label: 'Vehicle Number' },
    {
      key: 'vehicle_type',
      label: 'Type',
      render: (vehicle) => (
        <span className="uppercase text-xs font-medium">
          {vehicle.vehicle_type.replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (vehicle) => <StatusBadge status={vehicle.status} />,
    },
    {
      key: 'vehicle_contract_amount',
      label: 'Contract Amount',
      cellClassName: 'text-right',
      render: (vehicle) =>
        vehicle.vehicle_contract_amount != null
          ? `₹${Number(vehicle.vehicle_contract_amount).toLocaleString('en-IN')}`
          : '-',
    },
    {
      key: 'created_at',
      label: 'Created Date',
      render: (vehicle) =>
        new Date(vehicle.created_at).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
    },
  ];

  const getActions = (vehicle) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/vehicles/view/${vehicle.id}`),
    },
    {
      label: 'Edit Vehicle',
      icon: Pencil,
      onClick: () => navigate(`/vehicles/edit/${vehicle.id}`),
    },
    {
      label: 'Delete Vehicle',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(vehicle.id, vehicle.vehicle_name),
    },
  ];

  return (
    <PageLayout
      title="Vehicles"
      icon={Truck}
      actions={
        <Button icon={Plus} onClick={() => navigate('/vehicles/add')}>
          Add Vehicle
        </Button>
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Name or number...' },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
          },
          {
            key: 'vehicle_type',
            label: 'Vehicle Type',
            type: 'select',
            options: [
              { value: 'cargo_goods', label: 'Cargo Goods' },
              { value: 'bus', label: 'Bus' },
            ],
          },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() => setFilterValues({ search: '', status: '', vehicle_type: '' })}
      />

      <DataTable
        columns={columns}
        data={vehicles}
        loading={loading}
        emptyMessage="No vehicles found"
        rowKey="id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
}
